import type React from "react"
import { Building, Edit, Save, X } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { StateSelect } from "../ui/state-select"
import { Button } from "../ui/button"

interface FirmInformation {
  first_name: string
  last_name: string
  email: string
  law_firm: string
  state: string
}

interface EmailValidation {
  emailChecking: boolean
  emailAvailable: boolean | null
  emailMessage: string
}

interface FirmInformationCardProps {
  firmData: FirmInformation
  errors?: { [key: string]: string[] }
  onDataChange: (field: keyof FirmInformation, value: string) => void
  viewOnly?: boolean
  isEditing?: boolean
  onEdit?: () => void
  onSave?: (data: FirmInformation) => void
  onCancel?: () => void
  alwaysEditable?: boolean
  emailValidation?: EmailValidation
}

export default function FirmInformationCard({ 
  firmData, 
  errors = {}, 
  onDataChange,
  viewOnly = false,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  alwaysEditable = false,
  emailValidation
}: FirmInformationCardProps) {
  const handleInputChange = (field: keyof FirmInformation, value: string) => {
    onDataChange(field, value)
  }

  const handleSave = () => {
    if (onSave) {
      onSave(firmData)
    }
  }

  // Get email field styling based on validation state
  const getEmailFieldStyle = () => {
    if (!emailValidation) return errors.email ? "border-red-500" : ""
    
    if (emailValidation.emailChecking) return "border-slate-300"
    if (emailValidation.emailAvailable === true) return "border-green-500"
    if (emailValidation.emailAvailable === false) return "border-red-500"
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another firm") {
      return "border-red-500" // Format validation error
    }
    return errors.email ? "border-red-500" : ""
  }

  // Get email validation icon
  const getEmailIcon = () => {
    if (!emailValidation) return null
    
    if (emailValidation.emailChecking) {
      return <div className="h-4 w-4 text-slate-400 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
    }
    if (emailValidation.emailAvailable === true) {
      return <div className="h-4 w-4 text-green-500">✓</div>
    }
    if (emailValidation.emailAvailable === false) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    // Show error icon for format validation errors
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another firm") {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    return null
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Firm Information
          </CardTitle>
          {!alwaysEditable && (
            <div className="flex items-center gap-2">
              {viewOnly && !isEditing && onEdit && (
                <Button size="sm" onClick={onEdit} className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isEditing && (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button type="button" size="sm" onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
              First Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="first_name"
                value={firmData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                placeholder="Enter first name"
                className={errors.first_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900 break-words overflow-wrap-anywhere">{firmData.first_name}</p>
              </div>
            )}
            {errors.first_name && <p className="text-sm text-red-500">{errors.first_name[0]}</p>}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
              Last Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="last_name"
                value={firmData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                placeholder="Enter last name"
                className={errors.last_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900 break-words overflow-wrap-anywhere">{firmData.last_name}</p>
              </div>
            )}
            {errors.last_name && <p className="text-sm text-red-500">{errors.last_name[0]}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <div className="relative">
                <Input
                  id="email"
                  value={firmData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email address"
                  className={`pr-10 ${getEmailFieldStyle()}`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {getEmailIcon()}
                </div>
              </div>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900 break-words overflow-wrap-anywhere">{firmData.email}</p>
              </div>
            )}
            {/* Email validation message */}
            {emailValidation && emailValidation.emailMessage && (
              <p className={`text-sm ${
                emailValidation.emailAvailable === true ? 'text-green-600' : 
                emailValidation.emailAvailable === false ? 'text-red-600' : 
                emailValidation.emailMessage.includes('valid') || emailValidation.emailMessage.includes('cannot') ? 'text-red-600' : 
                'text-slate-600'
              }`}>
                {emailValidation.emailChecking ? "Checking email availability..." : emailValidation.emailMessage}
              </p>
            )}
            {errors.email && <p className="text-sm text-red-500">{errors.email[0]}</p>}
          </div>

          {/* State */}
          <div className="space-y-2">
            <Label htmlFor="state" className="text-sm font-medium text-slate-700">
              State *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <StateSelect
                value={firmData.state}
                onValueChange={(value) => handleInputChange("state", value)}
                placeholder="Select state"
                className={errors.state ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{firmData.state}</p>
              </div>
            )}
            {errors.state && <p className="text-sm text-red-500">{errors.state[0]}</p>}
          </div>

          {/* Law Firm */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="law_firm" className="text-sm font-medium text-slate-700">
              Law Firm *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="law_firm"
                value={firmData.law_firm}
                onChange={(e) => handleInputChange("law_firm", e.target.value)}
                placeholder="Enter law firm name"
                className={errors.law_firm ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900 break-words overflow-wrap-anywhere">{firmData.law_firm}</p>
              </div>
            )}
            {errors.law_firm && <p className="text-sm text-red-500">{errors.law_firm[0]}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 